import type { App } from "vue";
import type { Router } from "vue-router";
import type { ErrorEvent, EventHint } from "@sentry/core";

import * as Sentry from "@sentry/vue";
import {
  SPAN_STATUS_ERROR,
  SPAN_STATUS_OK,
} from "@sentry/core";

import { appConfig } from "@/config/appConfig";
import { logger } from "@/utils/logger";

type SentryLevel = "fatal" | "error" | "warning" | "info" | "debug";

const IGNORED_ERRORS: Array<string | RegExp> = [
  "ResizeObserver loop",
  "ResizeObserver loop completed with undelivered notifications",
  "Network Error",
  "NetworkError",
  "Failed to fetch",
  "Load failed",
  "Fetch failed",
  /Loading chunk .* failed/,
  "ChunkLoadError",
  "Non-Error promise rejection captured",
  "Non-Error exception captured",
  "TypeError: cancelled",
  "cancelled",
  /Extension context invalidated/,
  "Script error.",
  "SecurityError",
];

const DENIED_URLS: RegExp[] = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^edge:\/\//i,
];

const CHUNK_ERROR_SESSION_KEY: string = "chunk_load_error_reloaded";

const toError = (value: unknown): Error => {
  return value instanceof Error ? value : new Error(String(value));
};

const getMessage = (obj: object): string | undefined => {
  const descriptor: PropertyDescriptor | undefined =
    Object.getOwnPropertyDescriptor(obj, "message");
  const value: unknown = descriptor?.value;

  return typeof value === "string" ? value : undefined;
};

const isChunkLoadError = (error: unknown): boolean => {
  if (error && typeof error === "object") {
    const nameDesc: PropertyDescriptor | undefined =
      Object.getOwnPropertyDescriptor(error, "name");
    const nameVal: unknown = nameDesc?.value;

    if (nameVal === "ChunkLoadError") {
      return true;
    }

    const msg: string | undefined = getMessage(error);

    return msg !== undefined && msg.includes("Loading chunk");
  }

  return false;
};

const getComponentNameFromInstance = (instance: unknown): string => {
  if (instance === null || typeof instance !== "object") {
    return "Unknown";
  }

  const optsDesc: PropertyDescriptor | undefined =
    Object.getOwnPropertyDescriptor(instance, "$options");
  const opts: unknown = optsDesc?.value;

  if (opts && typeof opts === "object" && "name" in opts) {
    const nameDesc: PropertyDescriptor | undefined =
      Object.getOwnPropertyDescriptor(opts, "name");
    const nameVal: unknown = nameDesc?.value;

    if (typeof nameVal === "string") {
      return nameVal;
    }
  }

  return "Unknown";
};

const extractComponentName = (instance: unknown): string => {
  return getComponentNameFromInstance(instance);
};

const extractProps = (instance: unknown): unknown => {
  if (instance === null || typeof instance !== "object") {
    return undefined;
  }

  const propsDesc: PropertyDescriptor | undefined =
    Object.getOwnPropertyDescriptor(instance, "$props");

  return propsDesc?.value;
};


const toSentryContext = (
  rec: Record<string, unknown>,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};

  for (const [key, val] of Object.entries(rec)) {
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      out[key] = val;
    }
  }

  return out;
};

const handleChunkLoadInBeforeSend = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  if (!isChunkLoadError(hint.originalException)) {
    return event;
  }

  const hasReloaded: string | null = sessionStorage.getItem(
    CHUNK_ERROR_SESSION_KEY,
  );

  if (hasReloaded !== null) {
    return event;
  }

  sessionStorage.setItem(CHUNK_ERROR_SESSION_KEY, "true");
  window.location.reload();

  return null;
};

export const initSentry = (app: App, router: Router): void => {
  const sentryDsn: string | undefined = appConfig.sentry.dsn;
  const isEnabled: boolean =
    Boolean(appConfig.sentry.enabled) &&
    Boolean(sentryDsn) &&
    sentryDsn !== "your-dsn-here";

  if (isEnabled) {
    const environment: string = appConfig.sentry.environment;
    const tracesSampleRate: number = appConfig.sentry.tracesSampleRate;

    Sentry.init({
      app,
      dsn: sentryDsn,
      environment,
      release: appConfig.sentry.release,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        Sentry.browserProfilingIntegration(),
      ],
      tracesSampleRate,
      profilesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: IGNORED_ERRORS,
      denyUrls: DENIED_URLS,

      beforeSend(
        event: ErrorEvent,
        hint: EventHint,
      ): ErrorEvent | PromiseLike<ErrorEvent | null> | null {
        if (!import.meta.env.DEV || appConfig.sentry.devSend) {
          return handleChunkLoadInBeforeSend(event, hint);
        }

        return null;
      },
    });

    setAppContext({
      environment,
      version: appConfig.sentry.release || __APP_VERSION__,
    });

    const errorHandler = (
      err: unknown,
      instance: unknown,
      info: string,
    ): void => {
      logger.error("Vue Error:", err, info);

      captureException(toError(err), {
        tags: { errorType: "vue-error", lifecycle: info },
        extra: {
          componentName: extractComponentName(instance),
          propsData: extractProps(instance),
          lifecycle: info,
        },
      });
    };

    app.config.errorHandler = errorHandler;
  }

  if (import.meta.env.DEV) {
    const warnHandler = (
      msg: string,
      _instance: unknown,
      trace: string,
    ): void => {
      logger.warn("Vue Warning:", msg, trace);
    };

    app.config.warnHandler = warnHandler;
  }
};

export const isSentryAvailable = (): boolean => {
  return typeof Sentry !== "undefined" && Sentry.getClient?.() !== undefined;
};

interface SentryContext {
  level?: SentryLevel;
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
}

export const captureException = (
  error: Error,
  context: SentryContext = {},
): void => {
  if (isSentryAvailable()) {
    const { level = "error", tags = {}, extra = {} } = context;

    Sentry.captureException(error, {
      level,
      tags: toSentryContext(tags),
      extra: toSentryContext(extra),
    });
  } else {
    logger.warn("Sentry is not available. Error:", error);
  }
};

export const captureMessage = (
  message: string,
  level: SentryLevel = "info",
  context: Omit<SentryContext, "level"> = {},
): void => {
  if (isSentryAvailable()) {
    const { tags = {}, extra = {} } = context;

    Sentry.captureMessage(message, {
      level,
      tags: toSentryContext(tags),
      extra: toSentryContext(extra),
    });
  } else {
    logger.warn("Sentry is not available. Message:", message);
  }
};

interface SentryUser {
  id?: string;
  email?: string;
  username?: string;
  extra?: Record<string, unknown>;
}

export const setUser = (user: SentryUser | null): void => {
  if (!isSentryAvailable()) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);

    return;
  }

  const { extra, ...userBase } = user;

  Sentry.setUser(userBase);

  if (extra && Object.keys(extra).length > 0) {
    Sentry.setContext("user_details", extra);
  }
};

export const clearUser = (): void => {
  setUser(null);
};

interface SentryBreadcrumb {
  message?: string;
  category?: string;
  level?: SentryLevel;
  data?: Record<string, unknown>;
}

export const addBreadcrumb = (breadcrumb: SentryBreadcrumb): void => {
  if (isSentryAvailable()) {
    Sentry.addBreadcrumb({ level: "info", ...breadcrumb });
  }
};

export const setTag = (key: string, value: string): void => {
  if (isSentryAvailable()) {
    Sentry.setTag(key, value);
  }
};

export const setContext = (
  name: string,
  context: Record<string, unknown>,
): void => {
  if (isSentryAvailable()) {
    Sentry.setContext(name, context);
  }
};

export const setAppContext = (context: {
  tenantId?: string;
  environment?: string;
  version?: string;
  feature?: string;
}): void => {
  if (!isSentryAvailable()) {
    return;
  }

  const data: Record<string, string> = {
    environment: context.environment || import.meta.env.MODE,
    version: context.version || __APP_VERSION__,
  };

  if (context.tenantId) {
    data.tenantId = context.tenantId;
  }

  if (context.feature) {
    data.feature = context.feature;
  }

  Sentry.setContext("application", data);
};

export const testSentry = (): void => {
  if (isSentryAvailable()) {
    logger.log("Sending test error to Sentry...");

    addBreadcrumb({
      message: "User triggered Sentry test",
      category: "test",
      level: "info",
    });

    captureException(
      new Error(
        "Sentry Test Error - This is a test error from the development environment",
      ),
      {
        tags: { test: true, environment: import.meta.env.MODE },
        extra: {
          testTimestamp: new Date().toISOString(),
          testMessage: "This is a test error to verify Sentry integration",
        },
      },
    );

    logger.log("Test error sent! Check your Sentry dashboard.");
  } else {
    logger.error("Sentry is not initialized. Check your configuration.");
  }
};

interface SentryTransaction {
  setStatus: (status: string) => void;
  finish: () => void;
  setTag?: (key: string, value: string) => void;
}

const wrapSpanAsTransaction = (
  span: NonNullable<ReturnType<typeof Sentry.startInactiveSpan>>,
): SentryTransaction => {
  const transaction: SentryTransaction = {
    setStatus(status: string): void {
      span.setStatus?.({
        code: status === "ok" ? SPAN_STATUS_OK : SPAN_STATUS_ERROR,
        message: status,
      });
    },
    finish: (): void => {
      span.end?.();
    },
  };

  if (span.setAttribute) {
    transaction.setTag = (key: string, value: string): void => {
      span.setAttribute(key, value);
    };
  }

  return transaction;
};


export const startTransaction = (
  name: string,
  op: string = "custom",
): SentryTransaction | null => {
  if (!isSentryAvailable()) {
    return null;
  }

  try {
    const span: ReturnType<typeof Sentry.startInactiveSpan> | undefined =
      Sentry.startInactiveSpan?.({ name, op });

    if (span) {
      return wrapSpanAsTransaction(span);
    }

    return null;
  } catch {
    return null;
  }
};

export const monitorPerformance = <TArgs extends unknown[], TReturn>(
  transactionName: string,
  fn: (...args: TArgs) => Promise<TReturn>,
): ((...args: TArgs) => Promise<TReturn>) => {
  return async (...args: TArgs): Promise<TReturn> => {
    const transaction: SentryTransaction | null =
      startTransaction(transactionName);

    try {
      const result: TReturn = await fn(...args);

      transaction?.setStatus("ok");

      return result;
    } catch (error: unknown) {
      transaction?.setStatus("internal_error");
      captureException(toError(error), {
        tags: { transaction: transactionName },
      });

      throw error;
    } finally {
      transaction?.finish();
    }
  };
};

if (import.meta.env.DEV && typeof window !== "undefined") {
  window.testSentry = testSentry;

  logger.log(
    "%c💡 Tip: Run window.testSentry() to test Sentry integration",
    "color: #3b82f6; font-weight: bold;",
  );
}
